/**
 * データエクスポート用ユーティリティ
 */

import { DataSet, DataValue, DataType } from '../../types';
import { getColumnData } from './column-utils';
import { ROW_ID_COLUMN_ID } from '../../types/system-columns';
import logger from './logger';

export type ExportFormat = 'csv' | 'tsv' | 'json';

export interface ExportOptions {
  format: ExportFormat;
  includeHeaders?: boolean;
  selectedRowsOnly?: boolean;
  selectedRows?: number[];
  filteredRowIndices?: number[] | null;
  sortedRowIndices?: number[];
}

/**
 * データセットをエクスポート用の文字列に変換
 */
export function exportDataset(
  dataset: DataSet,
  options: ExportOptions
): string {
  const {
    format,
    includeHeaders = true,
    selectedRowsOnly = false,
    selectedRows = [],
    filteredRowIndices = null,
    sortedRowIndices
  } = options;

  logger.info('[dataExportUtils] Exporting dataset', {
    datasetId: dataset.id,
    format,
    includeHeaders,
    selectedRowsOnly,
    selectedRowsCount: selectedRows.length,
    hasFilteredRows: !!filteredRowIndices,
    hasSortedRows: !!sortedRowIndices
  });

  // エクスポート対象の行インデックスを決定
  let rowIndices: number[];
  
  if (selectedRowsOnly && selectedRows.length > 0) {
    // 選択行のみ
    rowIndices = [...selectedRows].sort((a, b) => a - b);
  } else if (sortedRowIndices) {
    // ソート済みの行インデックスを使用
    rowIndices = sortedRowIndices;
  } else if (filteredRowIndices) {
    // フィルタ済みの行インデックスを使用
    rowIndices = filteredRowIndices;
  } else {
    // 全行
    const firstColumn = dataset.schema.columns.find(col => col.id !== ROW_ID_COLUMN_ID);
    if (!firstColumn) {
      logger.warn('[dataExportUtils] No data columns found');
      return '';
    }
    const columnData = getColumnData(dataset, firstColumn.id);
    const rowCount = columnData?.length || 0;
    rowIndices = Array.from({ length: rowCount }, (_, i) => i);
  }

  // RowNumber列を除外したカラム
  const columns = dataset.schema.columns.filter(col => col.id !== ROW_ID_COLUMN_ID);

  switch (format) {
    case 'csv':
      return exportToCSV(dataset, columns, rowIndices, includeHeaders);
    case 'tsv':
      return exportToTSV(dataset, columns, rowIndices, includeHeaders);
    case 'json':
      return exportToJSON(dataset, columns, rowIndices);
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
}

/**
 * エクスポート用に値をフォーマット
 * 表示用のformatDataValueと異なり、NULL表記やdatetime形式を調整
 */
function formatValueForExport(value: DataValue, dataType?: DataType): string {
  if (value === null || value === undefined) {
    return 'null';  // 小文字のnull
  }

  // datetime型はそのまま（ISO形式を保持）
  if (dataType === 'datetime' || dataType === 'date') {
    return String(value);
  }

  // boolean型
  if (dataType === 'boolean') {
    return String(value);
  }

  // 数値型
  if (dataType === 'int64' || dataType === 'float64') {
    // 整数の場合は小数点なし、小数の場合は適切な精度で
    const num = Number(value);
    if (Number.isInteger(num)) {
      return num.toString();
    }
    // 小数点以下の無駄な0を削除
    return num.toString();
  }

  // その他はそのまま文字列化
  return String(value);
}

/**
 * CSV形式でエクスポート
 */
function exportToCSV(
  dataset: DataSet,
  columns: DataSet['schema']['columns'],
  rowIndices: number[],
  includeHeaders: boolean
): string {
  const lines: string[] = [];
  
  // ヘッダー行
  if (includeHeaders) {
    const headerRow = columns.map(col => escapeCSVValue(col.name));
    lines.push(headerRow.join(','));
  }
  
  // データ行
  for (const rowIndex of rowIndices) {
    const row: string[] = [];
    for (const column of columns) {
      const columnData = getColumnData(dataset, column.id);
      const value = columnData?.[rowIndex] ?? null;
      // エクスポート用にフォーマット（NULL -> null、datetimeはそのまま）
      const formattedValue = formatValueForExport(value, column.type);
      row.push(escapeCSVValue(formattedValue));
    }
    lines.push(row.join(','));
  }
  
  return lines.join('\r\n'); // CRLF for Windows compatibility
}

/**
 * TSV形式でエクスポート
 */
function exportToTSV(
  dataset: DataSet,
  columns: DataSet['schema']['columns'],
  rowIndices: number[],
  includeHeaders: boolean
): string {
  const lines: string[] = [];
  
  // ヘッダー行
  if (includeHeaders) {
    const headerRow = columns.map(col => escapeTSVValue(col.name));
    lines.push(headerRow.join('\t'));
  }
  
  // データ行
  for (const rowIndex of rowIndices) {
    const row: string[] = [];
    for (const column of columns) {
      const columnData = getColumnData(dataset, column.id);
      const value = columnData?.[rowIndex] ?? null;
      // エクスポート用にフォーマット（NULL -> null、datetimeはそのまま）
      const formattedValue = formatValueForExport(value, column.type);
      row.push(escapeTSVValue(formattedValue));
    }
    lines.push(row.join('\t'));
  }
  
  return lines.join('\r\n');
}

/**
 * JSON形式でエクスポート
 */
function exportToJSON(
  dataset: DataSet,
  columns: DataSet['schema']['columns'],
  rowIndices: number[]
): string {
  const rows: Record<string, unknown>[] = [];
  
  for (const rowIndex of rowIndices) {
    const row: Record<string, unknown> = {};
    for (const column of columns) {
      const columnData = getColumnData(dataset, column.id);
      const value = columnData?.[rowIndex] ?? null;
      row[column.name] = value;
    }
    rows.push(row);
  }
  
  return JSON.stringify(rows, null, 2);
}

/**
 * CSV値のエスケープ
 */
function escapeCSVValue(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * TSV値のエスケープ
 */
function escapeTSVValue(value: string): string {
  // タブ、改行、キャリッジリターンをエスケープ
  return value
    .replace(/\t/g, '\\t')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
}

/**
 * エクスポート形式の拡張子を取得
 */
export function getExportFileExtension(format: ExportFormat): string {
  switch (format) {
    case 'csv':
      return '.csv';
    case 'tsv':
      return '.tsv';
    case 'json':
      return '.json';
    default:
      return '.txt';
  }
}

/**
 * エクスポート形式の表示名を取得
 */
export function getExportFormatName(format: ExportFormat): string {
  switch (format) {
    case 'csv':
      return 'CSV (Comma-separated values)';
    case 'tsv':
      return 'TSV (Tab-separated values)';
    case 'json':
      return 'JSON (JavaScript Object Notation)';
    default:
      return 'Unknown format';
  }
}